import { calculateSurcharge } from "@/utils/pricingUtils";
import { productDetailHref } from "@/lib/productDetailHref";
import { isServiceProduct } from "@/lib/isServiceProduct";
import ServiceCard from "@/components/ServiceCard";
import Link from "next/link";
import Image from "next/image";
import { memo, useEffect, useMemo, useState } from "react";

const formatPrice = (price) => {
  const numericPrice = Number(price); // Ensure it's a number
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericPrice);
};

const ProductCardInner = memo(({ data, lcp = false, prefetchProduct = true, prefetchShop = true }) => {
  const [pricingEpoch, setPricingEpoch] = useState(0);
  /** Avoid SSR/client markup mismatch: first paint matches server (empty user prefs), then hydrate prices/badges. */
  const [userPrefsHydrated, setUserPrefsHydrated] = useState(false);
  useEffect(() => {
    const bump = () => setPricingEpoch((n) => n + 1);
    window.addEventListener("userLoggedIn", bump);
    return () => window.removeEventListener("userLoggedIn", bump);
  }, []);
  useEffect(() => setUserPrefsHydrated(true), []);

  // Memoize calculations to avoid re-computation
  const { userInfo, userCurrency, processedData, path } = useMemo(() => {
    if (!data || typeof data !== "object") {
      return {
        userInfo: {},
        userCurrency: false,
        processedData: null,
        path: "",
      };
    }
    const userInfoData =
      typeof window !== "undefined" && userPrefsHydrated
        ? JSON.parse(localStorage.getItem("userInfo") || "{}")
        : {};
    const sellerCountry = data?.seller?.country || "";
    const userCurrency = sellerCountry ? (userInfoData?.currency && userInfoData?.currencyRate) ? userInfoData?.currency : false : false;
    const processedData = calculateSurcharge([data], {
      ignoreStoredUserInfo: !userPrefsHydrated,
    })?.[0];
    const href =
      productDetailHref(processedData || data) ||
      productDetailHref(data) ||
      "";

    return { userInfo: userInfoData, userCurrency, processedData, path: href };
  }, [data, pricingEpoch, userPrefsHydrated]);
  const productHref = useMemo(() => {
    if (!data) return "#";
    return path || "#";
  }, [path, data]);
  if (!data || typeof data !== "object") {
    return null;
  }

  // Slightly under “320px” slot on md+ shaves srcset width (Lighthouse 640px vs ~398px box on some grids).
  const imageUrl = processedData?.images?.[0]?.imageUrl || "/placeholder.jpg";
  const cardImageSizes =
    "(max-width: 639px) min(92vw, 320px), min(300px, min(100vw, 320px))";
  const productTitle = processedData?.productName?.trim();
  const imageLinkLabel = productTitle ? `View ${productTitle}` : "View product";

  return (
    <div className="relative mx-auto flex w-full min-w-0 max-w-[320px] shrink-0 flex-col text-left">
      <Link
        href={productHref}
        aria-label={imageLinkLabel}
        className="group relative block w-full shrink-0 cursor-pointer"
        prefetch={prefetchProduct}
        scroll={true}
      >
        {/*
          Inner box owns aspect-ratio so height isn’t lost when all media layers are position:absolute.
          Next/Image `fill` needs this wrapper to stay a strict square for every card width.
        */}
        <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-slate-200 bg-orange-50/80">
          <div className="absolute inset-0 z-0 overflow-hidden [&>span]:block [&>span]:!h-full [&>span]:!w-full [&>span]:max-h-full [&_img]:!h-full [&_img]:!w-full [&_img]:max-h-none [&_img]:object-cover [&_img]:object-center">
            <Image
              src={imageUrl}
              alt=""
              fill
              className="object-cover object-center"
              sizes={cardImageSizes}
              priority={lcp}
              fetchPriority={lcp ? "high" : "auto"}
              loading={lcp ? undefined : "lazy"}
              decoding="async"
              quality={lcp ? 66 : 56}
              unoptimized={imageUrl.startsWith("data:")}
            />
          </div>
          <div
            className="absolute inset-0 z-[1] bg-yellow-950/[55%] opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none"
            aria-hidden
          />
          <div className="absolute bottom-9 left-0 right-0 z-20 flex items-center justify-center">
            <span className="pointer-events-none opacity-0 transition-opacity buttonprimary group-hover:opacity-100">
              Shop Now
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="7.477"
                height="13.14"
                viewBox="0 0 7.477 13.14"
                className="inline"
              >
                <path
                  id="Down_Arrow_3_"
                  d="M26.166,46.727a.559.559,0,0,1-.4-.164l-5.606-5.606a.561.561,0,0,1,.793-.793l5.21,5.21,5.21-5.21a.561.561,0,0,1,.793.793l-5.606,5.606a.559.559,0,0,1-.4.164Z"
                  transform="translate(-39.625 32.764) rotate(-90)"
                  fill="#fff"
                  stroke="#fff"
                  strokeWidth="0.75"
                />
              </svg>
            </span>
          </div>
        </div>
      </Link>
      <div className="flex flex-col">
        <p className="mt-3 mb-1 min-w-0 text-left text-sm font-normal leading-snug text-blue-950 md:text-base lg:text-lg">
          <Link
            href={productHref}
            prefetch={prefetchProduct}
            scroll={true}
            className="block truncate hover:text-primary"
          >
            {processedData?.productName}
          </Link>
        </p>
        <div className="mb-1 text-left text-sm font-bold leading-snug text-blue-950 md:text-base lg:text-lg">
          {processedData?.productType !== "Customizable" &&
          processedData?.discountCode &&
          parseFloat(processedData.discountCode) > 0 ? (
            <>
              <div>
                <span className="text-blue-950 font-bold mr-2">
                 {userCurrency ? userCurrency : "CA$"}{" "}
                  {formatPrice(parseFloat((processedData?.surTotalAmount ? processedData?.surTotalAmount : (processedData.finalPrice ? processedData.finalPrice : processedData.price)) || 0).toFixed(2))}
                </span>
                <span className="text-red-700 font-normal line-through  text-sm">
                  {" "}
                  {userCurrency ? userCurrency : "CA$"}{" "}
                  {
                    formatPrice(parseFloat((processedData?.surTotalAmountBDis ? processedData?.surTotalAmountBDis : (processedData?.totalPrice ? processedData?.totalPrice : processedData.price)) || 0).toFixed(2))
                  }
                </span>
                <span>
                  {" "}
                  <span className="text-orange-700 font-normal text-sm">
                    {" "}
                    ({processedData.discountCode}% off)
                  </span>
                </span>
              </div>
            </>
          ) : (
            <>
              <>
                {processedData &&
                  processedData.productType === "Customizable" &&
                  processedData.variations &&
                  processedData.variations.length && (
                    <>
                      <span className="text-blue-950 font-bold mr-2">
                       {userCurrency ? userCurrency : "CA$"}{" "}
                        {
                          formatPrice(parseFloat((processedData.variations[0]?.surTotalAmount
                            ? processedData.variations[0]?.surTotalAmount
                            : (processedData.variations[0].finalPrice
                              ? processedData.variations[0].finalPrice 
                              :processedData.variations[0].price))))
                        }
                      </span>
                      {processedData.discountCode &&
                        parseFloat(processedData.discountCode) > 0 && (
                          <>
                            <span className="text-red-700 font-normal line-through  text-sm">
                              {" "}
                              {userCurrency ? userCurrency : "CA$"}{" "}
                              {
                                formatPrice(parseFloat((processedData.variations[0].surTotalAmountBDis ? processedData.variations[0].surTotalAmountBDis : (processedData.variations[0].totalPrice ? processedData.variations[0].totalPrice : processedData.variations[0].price)).toFixed(2)))
                              }
                            </span>
                            <span>
                              {" "}
                              <span className="text-orange-700 font-normal text-sm">
                                {" "}
                                ({processedData.discountCode}% off)
                              </span>
                            </span>
                          </>
                        )}
                    </>
                  )}
              </>
              <>
                {processedData?.productType !== "Customizable" && (
                  <>{userCurrency ? userCurrency : "CA$"}{" "}
                  {formatPrice(parseFloat((processedData?.surTotalAmount ? processedData?.surTotalAmount : (processedData.totalPrice ? processedData.totalPrice : processedData.price))))}</>
                )}
              </>
            </>
          )}
        </div>
        <p className="mb-1 text-left text-sm text-slate-600 underline">
          <Link
            href={`/shop/${processedData?.seller?.storeSlug}`}
            prefetch={prefetchShop}
            className="inline-block"
          >
            {processedData?.seller?.firstName} {processedData?.seller?.lastName}
          </Link>
        </p>
        {(((( processedData?.seller?.country == userInfo?.country &&
          processedData?.seller?.shippingConfigId?.domestic?.flat_rate &&
          processedData?.seller?.shippingConfigId?.domestic?.flat_rate_options?.free_shipping) ||
          ( processedData?.seller?.country != userInfo?.country &&
          processedData?.seller?.shippingConfigId?.international?.flat_rate &&
          processedData?.seller?.shippingConfigId?.international?.flat_rate_options?.free_shipping) ||
          (processedData?.freeDelivery && processedData?.seller?.country == userInfo?.country)) &&
          processedData.productType != "Downloadable"
          ) ||
          processedData?.productType === "Downloadable") && (
          <div className="mb-0 flex flex-col gap-1">
            {((( processedData?.seller?.country == userInfo?.country &&
              processedData?.seller?.shippingConfigId?.domestic?.flat_rate &&
              processedData?.seller?.shippingConfigId?.domestic?.flat_rate_options?.free_shipping) ||
              ( processedData?.seller?.country != userInfo?.country &&
              processedData?.seller?.shippingConfigId?.international?.flat_rate &&
              processedData?.seller?.shippingConfigId?.international?.flat_rate_options?.free_shipping) ||
              (processedData?.freeDelivery && processedData?.seller?.country == userInfo?.country)) &&
              processedData.productType != "Downloadable"
              ) ? (
              <div className="px-[8px] py-[2px] text-[11px] bg-[#a0e193] rounded-full w-[max-content]">
                Free Shipping
              </div>
            ) : null}
            {processedData?.productType === "Downloadable" ? (
              <div className="px-[8px] py-[2px] text-[11px] bg-primary rounded-full w-[max-content] text-white">
                Downloadable
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
});

ProductCardInner.displayName = "ProductCardInner";

function ProductCardComponent(props) {
  if (isServiceProduct(props.data)) {
    return (
      <ServiceCard
        data={props.data}
        lcp={props.lcp}
        prefetchService={props.prefetchProduct}
        prefetchShop={props.prefetchShop}
      />
    );
  }
  return <ProductCardInner {...props} />;
}

export default memo(ProductCardComponent);
